import { Link } from "react-router-dom";
import { TopMenu } from "../../top-menu/TopMenu";
import { Footer } from "../../footer/Footer";
import { PageHero } from "../../page-hero/PageHero";
import CompanyImage from '../../../assets/company.png';
import AISC from "../../../assets/aisc.png";
import NISD from "../../../assets/nisd.png";
import SDS from "../../../assets/sds2_certification-badge__Detailer.png";

const MILESTONES = [
    { year: '1993', label: 'Founded', detail: 'Jim Truitt founds Rocky Point Detailing & Aircad in Las Vegas.' },
    { year: '1998', label: 'SDS2 adoption', detail: 'Among the early firms to adopt SDS2 for modeling and detailing.' },
    { year: '2010s', label: 'BIM expansion', detail: 'Added BIM coordination and Tekla PowerFab integration to the stack.' },
    { year: 'Today', label: '500+ projects', detail: 'Trusted by fabricators and GCs across the western U.S.' },
];

export const Company = () => (
    <div className="min-h-screen flex flex-col bg-white">
        <TopMenu />

        <PageHero
            eyebrow="Our Company"
            title="Three decades of"
            accent="getting steel right."
            subtitle="Rocky Point Detailing LLC & Aircad LLC have been delivering accurate shop drawings to steel fabricators since 1993 — through every shift in software, standards, and delivery expectations."
        />

        <main className="flex-1">
            <section className="py-20 md:py-28">
                <div className="container-px mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-7">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Our Story</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-6">
                            On the leading edge since day one.
                        </h2>
                        <div className="space-y-5 text-steel-700 text-lg leading-relaxed">
                            <p>
                                We have always operated at the leading edge of technology — utilizing the latest in CAD, modeling,
                                printing, scanning, and BIM since 1993.
                            </p>
                            <p>
                                Our staff has hands-on experience across every phase of structural and miscellaneous steel:
                                modeling, detailing, fabrication, and erection. That breadth shows up in drawings that
                                anticipate the questions your shop floor would ask.
                            </p>
                            <p>
                                We specialize in <strong className="text-navy-900">complex structural and miscellaneous steel</strong> —
                                preparing high-quality shop drawings and models that enable seamless fabrication and erection for
                                commercial, public works, and private projects.
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="rounded-2xl overflow-hidden shadow-card border border-steel-100">
                            <img src={CompanyImage} alt="Rocky Point Detailing team" className="w-full h-auto object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-steel-50">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="max-w-3xl mb-14">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Our Journey</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-4">
                            A track record built one project at a time.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {MILESTONES.map((m) => (
                            <div key={m.year} className="bg-white rounded-xl p-6 border border-steel-100 shadow-card">
                                <div className="text-3xl font-display font-bold text-navy-900 mb-1">{m.year}</div>
                                <div className="text-sm font-semibold text-accent-hover uppercase tracking-wider mb-3">{m.label}</div>
                                <p className="text-sm text-steel-600 leading-relaxed m-0">{m.detail}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="container-px mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <div className="rounded-2xl overflow-hidden shadow-card border border-steel-100 aspect-[4/5] bg-navy-900 flex items-center justify-center text-white">
                            <div className="text-center p-10">
                                <div className="w-24 h-24 rounded-full bg-accent/20 mx-auto mb-6 flex items-center justify-center">
                                    <span className="text-4xl font-display font-bold text-accent">JT</span>
                                </div>
                                <p className="text-lg font-display font-semibold mb-1">Jim Truitt</p>
                                <p className="text-sm text-steel-300 mb-4">Founder & CEO</p>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Leadership</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-6">
                            Meet Jim Truitt.
                        </h2>
                        <div className="space-y-5 text-steel-700 text-lg leading-relaxed">
                            <p>
                                A respected leader in the steel industry, Jim founded Rocky Point Detailing LLC and Aircad LLC
                                in 1993. Under his leadership, the firm has successfully completed <strong className="text-navy-900">over 500 steel
                                detailing projects</strong>.
                            </p>
                            <p>
                                Jim serves as a board member for the <strong className="text-navy-900">National Institute of Steel Detailers (NISD)</strong>,
                                where he is an active advocate for quality and training — working to educate industry
                                stakeholders on best practices.
                            </p>
                            <p>
                                His educational outreach has helped train hundreds of industry professionals. When he's not in
                                the office, Jim is a private pilot and enjoys flying to visit clients and jobsites throughout
                                the western United States.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-steel-50">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="max-w-3xl mb-14">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Credentials</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-4">
                            Certified by the bodies that matter.
                        </h2>
                        <p className="text-lg text-steel-600">
                            Every drawing we ship reflects the standards your project will be measured against.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-8 border border-steel-100 shadow-card">
                            <h3 className="text-xl font-bold font-display text-navy-900 mb-6">Certifications</h3>
                            <div className="flex items-center gap-8 mb-6">
                                <img src={SDS} alt="SDS2 Certified Detailer" className="h-24 object-contain" />
                            </div>
                            <ul className="space-y-2 text-steel-700">
                                <li className="flex items-start gap-2">
                                    <Check />
                                    NISD Class I & II Detailers
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check />
                                    SDS2 Certified Detailers
                                </li>
                            </ul>
                        </div>

                        <div className="bg-white rounded-2xl p-8 border border-steel-100 shadow-card">
                            <h3 className="text-xl font-bold font-display text-navy-900 mb-6">Memberships</h3>
                            <div className="flex items-center gap-8 mb-6">
                                <img src={AISC} alt="AISC Member" className="h-20 object-contain" />
                                <img src={NISD} alt="NISD Member" className="h-20 object-contain" />
                            </div>
                            <ul className="space-y-2 text-steel-700">
                                <li className="flex items-start gap-2">
                                    <Check />
                                    American Institute of Steel Construction (AISC)
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check />
                                    National Institute of Steel Detailers (NISD)
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-24">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="bg-navy-900 rounded-2xl p-10 md:p-14 text-center text-white relative overflow-hidden">
                        <div className="absolute inset-0 bg-hero-radial opacity-50" />
                        <div className="relative">
                            <h2 className="text-display-md font-display mb-4">Want to work with a detailer who's been at this since 1993?</h2>
                            <p className="text-steel-200 mb-8 max-w-xl mx-auto">
                                Send us your drawings or specs — we'll respond with scope, timeline, and pricing within 48 hours.
                            </p>
                            <Link
                                to="/contact-us"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-signal text-signal-ink font-semibold no-underline hover:bg-signal-hover transition-all shadow-glow-accent hover:scale-[1.02]"
                            >
                                Request a Quote
                                <Arrow />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>

        <Footer />
    </div>
);

const Check = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A5BA8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="mt-1 flex-shrink-0">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const Arrow = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);
